import { collection, getDocs, doc, writeBatch, deleteDoc, query, where, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Cruise } from '../context/CruiseContext';

export interface DuplicateUserRecord {
  uid: string;
  email: string;
  displayName: string;
  cruiseCount: number;
  createdAt: string;
  isTestAccount: boolean;
}

export interface DuplicateUserGroup {
  email: string;
  records: DuplicateUserRecord[];
}

export interface OrphanedCruise {
  id: string;
  userId: string;
  cruiseLine: string;
  shipName: string;
  departureDate: string;
}

export interface OrphanedFriendLookup {
  id: string;
  uid: string;
  email: string;
  username: string;
}

export interface DataIntegrityScanResult {
  duplicateUsers: DuplicateUserGroup[];
  orphanedCruises: OrphanedCruise[];
  orphanedLookups: OrphanedFriendLookup[];
}

export const scanDatabaseIntegrity = async (): Promise<DataIntegrityScanResult> => {
  const result: DataIntegrityScanResult = {
    duplicateUsers: [],
    orphanedCruises: [],
    orphanedLookups: []
  };

  // 1. Fetch ALL users to build the master index
  const usersSnap = await getDocs(collection(db, 'users'));
  const validUserIds = new Set<string>();
  
  // Maps to find duplicates: email -> array of user records
  const usersByEmail: Record<string, any[]> = {};
  
  usersSnap.docs.forEach(doc => {
    const uid = doc.id;
    validUserIds.add(uid);
    
    const data = doc.data();
    // Normalize email for comparison
    const email = (data.email || data.consentData?.email || '').toLowerCase().trim();
    
    if (email) {
      if (!usersByEmail[email]) {
        usersByEmail[email] = [];
      }
      usersByEmail[email].push({ uid, data });
    }
  });

  // 2. Fetch ALL cruises to find orphans and count user cruises
  const cruisesSnap = await getDocs(collection(db, 'cruises'));
  const userCruiseCounts: Record<string, number> = {};
  
  cruisesSnap.docs.forEach(docSnap => {
    const cruise = docSnap.data() as Cruise;
    const cruiseId = docSnap.id;
    
    if (!validUserIds.has(cruise.userId)) {
      result.orphanedCruises.push({
        id: cruiseId,
        userId: cruise.userId,
        cruiseLine: cruise.cruiseLine || 'Unknown',
        shipName: cruise.shipName || 'Unknown',
        departureDate: cruise.departureDate || 'Unknown'
      });
    } else {
      userCruiseCounts[cruise.userId] = (userCruiseCounts[cruise.userId] || 0) + 1;
    }
  });

  // 3. Process Duplicate Users
  Object.entries(usersByEmail).forEach(([email, records]) => {
    if (records.length > 1) {
      // We found a duplicate!
      const duplicateRecords: DuplicateUserRecord[] = records.map(({ uid, data }) => ({
        uid,
        email,
        displayName: data.preferredDisplayName || data.displayName || 'Unknown',
        cruiseCount: userCruiseCounts[uid] || 0,
        createdAt: data.consentData?.timestamp 
          ? new Date(data.consentData.timestamp).toLocaleString(undefined, { 
              dateStyle: 'medium', 
              timeStyle: 'short' 
            }) 
          : 'Unknown',
        isTestAccount: !!data.isTestAccount
      }));
      
      // Sort logic: 
      // 1. Primary: Records with cruises come first
      // 2. Secondary: Most recent timestamp comes first
      duplicateRecords.sort((a, b) => {
        // First, check cruise counts
        if (b.cruiseCount !== a.cruiseCount) {
          return b.cruiseCount - a.cruiseCount;
        }
        
        // If counts are equal, use timestamp recency
        const timeA = records.find(r => r.uid === a.uid)?.data?.consentData?.timestamp || '';
        const timeB = records.find(r => r.uid === b.uid)?.data?.consentData?.timestamp || '';
        return timeB.localeCompare(timeA);
      });
      
      result.duplicateUsers.push({
        email,
        records: duplicateRecords
      });
    }
  });

  // 4. Fetch ALL friend lookups to find orphans
  const lookupSnap = await getDocs(collection(db, 'friendLookup'));
  lookupSnap.docs.forEach(docSnap => {
    const data = docSnap.data();
    if (data.uid && !validUserIds.has(data.uid)) {
      result.orphanedLookups.push({
        id: docSnap.id,
        uid: data.uid,
        email: data.email || 'Unknown',
        username: data.username || 'Unknown'
      });
    }
  });

  return result;
};

export const deleteUserDocument = async (uid: string): Promise<void> => {
  await deleteDoc(doc(db, 'users', uid));
};

export const deleteCruisesBatch = async (cruiseIds: string[]): Promise<void> => {
  if (cruiseIds.length === 0) return;
  
  // Firestore batches support up to 500 operations
  const batches = [];
  let currentBatch = writeBatch(db);
  let opCount = 0;
  
  for (const id of cruiseIds) {
    currentBatch.delete(doc(db, 'cruises', id));
    opCount++;
    
    if (opCount === 500) {
      batches.push(currentBatch.commit());
      currentBatch = writeBatch(db);
      opCount = 0;
    }
  }
  
  if (opCount > 0) {
    batches.push(currentBatch.commit());
  }
  
  await Promise.all(batches);
};

export const deleteFriendLookupsBatch = async (lookupIds: string[]): Promise<void> => {
  if (lookupIds.length === 0) return;
  
  const batches = [];
  let currentBatch = writeBatch(db);
  let opCount = 0;
  
  for (const id of lookupIds) {
    currentBatch.delete(doc(db, 'friendLookup', id));
    opCount++;
    
    if (opCount === 500) {
      batches.push(currentBatch.commit());
      currentBatch = writeBatch(db);
      opCount = 0;
    }
  }
  
  if (opCount > 0) {
    batches.push(currentBatch.commit());
  }
  
  await Promise.all(batches);
};

export interface UnlinkedCruiseGroup {
  cruiseLine: string;
  shipName: string;
  count: number;
  sampleIds: string[];
}

/**
 * Scan all user cruises to find any ship names that are not in the official cruiseMetadata collections.
 */
export const scanUnlinkedCruises = async (cruiseLinesMetadata: any[]): Promise<UnlinkedCruiseGroup[]> => {
  const cruisesSnap = await getDocs(collection(db, 'cruises'));
  
  // Build a map of cruise line -> Set of lowercase ship names
  const officialShipsMap = new Map<string, Set<string>>();
  
  cruiseLinesMetadata.forEach(cl => {
    const shipSet = new Set(
      [...(cl.ships || []), ...(cl.retiredShips || [])].map((s: string) => s.toLowerCase().trim())
    );
    officialShipsMap.set(cl.name.toLowerCase().trim(), shipSet);
  });

  const unlinkedMap = new Map<string, { cruiseLine: string; shipName: string; count: number; sampleIds: string[] }>();

  cruisesSnap.docs.forEach(docSnap => {
    const data = docSnap.data();
    const id = docSnap.id;
    const cruiseLine = (data.cruiseLine || '').trim();
    const shipName = (data.shipName || '').trim();

    if (!cruiseLine || !shipName) return;

    const cruiseLineLower = cruiseLine.toLowerCase();
    const shipNameLower = shipName.toLowerCase();

    const officialShips = officialShipsMap.get(cruiseLineLower);
    const isLinked = officialShips && officialShips.has(shipNameLower);

    if (!isLinked) {
      const key = `${cruiseLine} | ${shipName}`;
      if (!unlinkedMap.has(key)) {
        unlinkedMap.set(key, {
          cruiseLine,
          shipName,
          count: 0,
          sampleIds: []
        });
      }
      const group = unlinkedMap.get(key)!;
      group.count++;
      if (group.sampleIds.length < 5) {
        group.sampleIds.push(id);
      }
    }
  });

  return Array.from(unlinkedMap.values()).sort((a, b) => b.count - a.count);
};

/**
 * Mass update user cruises to a new/correct ship name.
 */
export const massRenameUserCruises = async (
  cruiseLine: string,
  oldShipName: string,
  newShipName: string
): Promise<number> => {
  const q = query(
    collection(db, 'cruises'),
    where('cruiseLine', '==', cruiseLine),
    where('shipName', '==', oldShipName)
  );
  const snap = await getDocs(q);
  
  if (snap.empty) return 0;

  const batches = [];
  let currentBatch = writeBatch(db);
  let opCount = 0;

  for (const docSnap of snap.docs) {
    currentBatch.update(doc(db, 'cruises', docSnap.id), { shipName: newShipName });
    opCount++;

    if (opCount === 500) {
      batches.push(currentBatch.commit());
      currentBatch = writeBatch(db);
      opCount = 0;
    }
  }

  if (opCount > 0) {
    batches.push(currentBatch.commit());
  }

  await Promise.all(batches);
  return snap.size;
};

/**
 * Add a ship name to the official cruiseMetadata ships array.
 */
export const addShipToOfficialMetadata = async (
  cruiseLine: string,
  shipName: string
): Promise<void> => {
  const q = query(collection(db, 'cruiseMetadata'), where('name', '==', cruiseLine));
  const snap = await getDocs(q);

  if (snap.empty) {
    throw new Error(`Cruise line "${cruiseLine}" not found in metadata`);
  }

  const docSnap = snap.docs[0];
  const data = docSnap.data();
  const ships: string[] = data.ships || [];

  if (!ships.includes(shipName)) {
    ships.push(shipName);
    ships.sort();
    
    const shipMetadata = data.shipMetadata || {};
    if (!shipMetadata[shipName]) {
      shipMetadata[shipName] = {};
    }

    await updateDoc(doc(db, 'cruiseMetadata', docSnap.id), {
      ships,
      shipMetadata
    });
  }
};

/**
 * Mass delete user cruises matching a criteria.
 */
export const massDeleteUserCruises = async (
  cruiseLine: string,
  shipName: string
): Promise<number> => {
  const q = query(
    collection(db, 'cruises'),
    where('cruiseLine', '==', cruiseLine),
    where('shipName', '==', shipName)
  );
  const snap = await getDocs(q);

  if (snap.empty) return 0;

  const ids = snap.docs.map(doc => doc.id);
  await deleteCruisesBatch(ids);
  return ids.length;
};

